<script setup lang="ts">
import { WidgetSidebarRates } from '~/components/widget/sidebar-rates'
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { WidgetSelectDepositType } from '~/components/widget/select-deposit-type'
import type { IResponseData } from '~/types'
import { WidgetDepositsListMeiliSearch } from '~/components/widget/deposits'
import { WidgetDepositsSearchAliases } from '~/components/widget/deposits-search-aliases'
import { WidgetDepositSelectionCta } from '~/components/widget/deposit-selection-cta'
import type { IDepositsQueryParams, IDepositType } from '~/components/entity/deposit/item'
import { WidgetSeoContent, WidgetSeoHeroBanner } from '~/components/widget/seo'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { heroBanner } from '~/data/pages/deposits/mocks'
import { WidgetFaqList } from '~/components/widget/faq-list'
import { WidgetDiscussionsAskSpecialist } from '~/components/widget/discussions'
import { WidgetScrollTo } from '~/components/widget/scroll-to'
import { ANCHOR_IDS } from '~/lib/constants'
import {
  WidgetMonitorDepositRates,
  WidgetMonitorNbkrDynamics
} from '~/components/widget/monitor'

definePageMeta({
  path: '/deposits',
  alias: ['/deposits/:type(.*)*']
})

const route = useRoute()
const { t } = useI18n()
const { pathWithoutLocale } = useLocalizedPath()

const pageInfo = reactive({
  title: t('page.deposits.default_title')
})

const depositsListQueryParams = reactive<IDepositsQueryParams>({
  per_page: '20'
})

const typeParam = computed(() =>
  Array.isArray(route.params.type) ? route.params.type[0] : route.params.type
)

// Keyed by type: a shared key would serve the previously opened type's payload
// when switching between them.
const { data: depositType } = await useApi<IResponseData<IDepositType>>(
  () => `/deposit_types/${typeParam.value}`,
  {
    key: () => `deposits-deposit-types-${typeParam.value}`,
    immediate: !!typeParam.value,
    watch: [typeParam]
  }
)

watchEffect(() => {
  pageInfo.title = typeParam.value
    ? (depositType.value?.data.title ?? '')
    : t('page.deposits.default_title')
})

if (typeParam.value && !depositType.value) {
  showError({ statusCode: 404 })
}

const breadcrumbs = computed(() => {
  return typeParam.value
    ? [
        { title: t('page.deposits.breadcrumbs_title'), url: '/deposits' },
        { title: pageInfo.title, url: route.path }
      ]
    : [{ title: t('page.deposits.breadcrumbs_title'), url: '/deposits' }]
})
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetSelectDepositType :query-params="{ 'filter[position]': 'sidebar' }" />

            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />

            <SidebarBlock variant="white" :heading-text="$t('common.exchange_rates')">
              <WidgetSidebarRates />
            </SidebarBlock>

            <WidgetScrollTo
              :title="t('monitor.sidebar_scroll_to.title')"
              :description="t('monitor.sidebar_scroll_to.description')"
              :button-text="t('monitor.sidebar_scroll_to.button_text')"
              :target="ANCHOR_IDS.WIDGET_MARKET_MONITOR"
            />

            <WidgetDiscussionsAskSpecialist />
          </aside>
        </template>
        <div role="main" class="space-y-5">
          <WidgetSeoContent v-if="typeParam" position="upper" :title="pageInfo.title" />

          <WidgetSeoHeroBanner
            v-if="!typeParam"
            :heading="$t('page.deposits.breadcrumbs_title')"
            :text="$t('page.deposits.breadcrumbs_title')"
            :image="heroBanner.image"
          />

          <WidgetReviveAd zoneid="39" class="overflow-hidden rounded-lg" />

          <WidgetDepositsSearchAliases />

          <WidgetDepositSelectionCta />

          <WidgetDepositsListMeiliSearch :query-params="depositsListQueryParams" />

          <div :id="ANCHOR_IDS.WIDGET_MARKET_MONITOR" class="space-y-5">
            <WidgetMonitorDepositRates />
            <WidgetMonitorNbkrDynamics product="deposit" />
          </div>

          <WidgetSeoContent position="bottom" />

          <WidgetFaqList
            :query-params="{
              'filter[faqable_url]': pathWithoutLocale
            }"
          />
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
